import { createSelector } from '@reduxjs/toolkit';
import qs from 'query-string';
import { lruMemoize, weakMapMemoize } from 'reselect';

import { createAppSelector } from 'wanderu-ducks-utils';

import {
    getFilterQueryParams,
} from '../filterData/filterUtils';

function _selectUrlDirection(props) {
    return props.match.params.direction;
}

function _selectUrlOriginText(props) {
    return decodeURIComponent(props.match.params.originText);
}

function _selectUrlDestinationText(props) {
    return decodeURIComponent(props.match.params.destinationText);
}

function _selectUrlDepartDate(props) {
    return props.match.params.departDate;
}

function _selectUrlReturnDate(props) {
    return props.match.params.returnDate;
}

function _selectQuery(props) {
    return props.location.search;
}

const _selectParsedQuery = createAppSelector(
    _selectQuery,
    (query) => typeof query === 'string'
        ? qs.parse(query)
        : query,
);

const _selectUrlOrignPlaceId = createAppSelector(
    _selectParsedQuery,
    (parsedQuery) => parsedQuery.opid,
);

const _selectUrlDestinationPlaceId = createAppSelector(
    _selectParsedQuery,
    (parsedQuery) => parsedQuery.dpid,
);

const _selectUrlDepartTripId = createAppSelector(
    _selectParsedQuery,
    (parsedQuery) => parsedQuery.dtid,
);

const _selectUrlPassengers = createAppSelector(
    _selectParsedQuery,
    (parsedQuery) => parsedQuery.p,
);

const selectUrlSearchParams = createAppSelector(
    _selectUrlDirection,
    _selectUrlOriginText,
    _selectUrlOrignPlaceId,
    _selectUrlDestinationText,
    _selectUrlDestinationPlaceId,
    _selectUrlDepartDate,
    _selectUrlReturnDate,
    _selectUrlDepartTripId,
    _selectUrlPassengers,
    (...params) => ({
        departDate: params[5],
        departTripId: params[7],
        destinationPlaceId: params[4],
        destinationText: params[3],
        direction: params[0],
        originPlaceId: params[2],
        originText: params[1],
        passengers: params[8],
        returnDate: params[6],
    }),
);

// Uses lruMemoize(maxSize: 3) instead of the default maxSize: 1 from createAppSelector.
// mapStateToProps calls this selector with three distinct direction values (undefined, 'depart',
// 'return') within a single render pass. With maxSize: 1, each paired call evicts the previous
// slot, creating new object references on every render and triggering an infinite update loop.
// maxSize: 3 keeps all three direction results simultaneously while remaining SSR-safe (bounded).
// Note: memoizeOptions applies only to lruMemoize (inner layer); argsMemoize (weakMapMemoize)
// is intentionally unbounded — it is keyed on object references which are GC'd on unmount.
const selectUrlFilterParams = createSelector(
    [
        _selectQuery,
        (_, direction) => direction,
    ],
    (search, direction) => getFilterQueryParams(search, direction),
    { argsMemoize: weakMapMemoize, memoize: lruMemoize, memoizeOptions: { maxSize: 3 } },
);

export {
    selectUrlFilterParams,
    selectUrlSearchParams,
};
